import type { LegacyAudienceConfig } from '@/domain/audience/types'
import type { AudienceCapabilities, AudienceManifestV1, ResolvedAudienceSkinV1, RuntimeSource } from '@/domain/release/types'

export interface CachedRuntimeBundle {
  key: string
  stadiumId: string
  eventId: string
  releaseId: string
  source: RuntimeSource
  verifiedAt: string
  offlineValidUntil: string
  config: LegacyAudienceConfig
  skin: ResolvedAudienceSkinV1
  /** Optional multi-skin release data. Older cached bundles omit these fields. */
  skins?: Record<string, ResolvedAudienceSkinV1>
  skinOptions?: Array<{ skinId: string; name: string }>
  defaultSkinId?: string
  capabilities: AudienceCapabilities
}

const DATABASE_NAME = 'venue-h5-audience-v2-runtime-v1'
const DATABASE_VERSION = 1

export class RuntimeCache {
  private readonly database = openDatabase()

  async put(bundle: CachedRuntimeBundle): Promise<void> {
    const database = await this.database
    const transaction = database.transaction('bundles', 'readwrite')
    transaction.objectStore('bundles').put(bundle)
    await transactionDone(transaction)
  }

  async get(stadiumId: string, eventId: string): Promise<CachedRuntimeBundle | null> {
    const database = await this.database
    return request<CachedRuntimeBundle | undefined>(database.transaction('bundles').objectStore('bundles').get(`${stadiumId}::${eventId}`)).then((value) => value || null)
  }

  async getLast(stadiumId: string): Promise<CachedRuntimeBundle | null> {
    const database = await this.database
    const values = await request<CachedRuntimeBundle[]>(database.transaction('bundles').objectStore('bundles').index('by-stadium').getAll(stadiumId))
    values.sort((left, right) => Date.parse(right.verifiedAt) - Date.parse(left.verifiedAt))
    return values[0] || null
  }

  async putManifest(manifest: AudienceManifestV1): Promise<void> {
    const database = await this.database
    const transaction = database.transaction('manifests', 'readwrite')
    const store = transaction.objectStore('manifests')
    const existing = await request<AudienceManifestV1 | undefined>(store.get(manifest.stadiumId))
    if (!existing || Date.parse(manifest.updatedAt) >= Date.parse(existing.updatedAt)) store.put(manifest)
    await transactionDone(transaction)
  }

  async getManifest(stadiumId: string): Promise<AudienceManifestV1 | null> {
    const database = await this.database
    return request<AudienceManifestV1 | undefined>(database.transaction('manifests').objectStore('manifests').get(stadiumId)).then((value) => value || null)
  }
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION)
    request.onupgradeneeded = () => {
      const database = request.result
      if (!database.objectStoreNames.contains('bundles')) {
        const store = database.createObjectStore('bundles', { keyPath: 'key' })
        store.createIndex('by-stadium', 'stadiumId')
      }
      if (!database.objectStoreNames.contains('manifests')) database.createObjectStore('manifests', { keyPath: 'stadiumId' })
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error || new Error('IndexedDB 打开失败'))
    request.onblocked = () => reject(new Error('IndexedDB 升级被阻塞'))
  })
}

function request<T>(value: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    value.onsuccess = () => resolve(value.result)
    value.onerror = () => reject(value.error || new Error('IndexedDB 请求失败'))
  })
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error || new Error('IndexedDB 事务失败'))
    transaction.onabort = () => reject(transaction.error || new Error('IndexedDB 事务已取消'))
  })
}
